'use client';

import React, { useEffect, useRef } from 'react';

const consumerSteps = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
    step: '01',
    title: 'Búsca lo que necesitas',
    desc: 'Escribe el producto o servicio que buscas. AQUÍ APP te muestra resultados instantáneos en tu zona.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    step: '02',
    title: 'Conecta con el más cercano',
    desc: 'Ve la ubicación exacta, confirma disponibilidad y contacta directamente al vendedor antes de ir.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
    step: '03',
    title: '¡Compra con confianza!',
    desc: 'Realiza tu compra sabiendo exactamente qué hay disponible, dónde está y a qué precio. Sin sorpresas.',
  },
];

const partnerSteps = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
      </svg>
    ),
    step: '01',
    title: 'Únete como aliado',
    desc: 'Registra tu negocio en minutos. Sube tu catálogo de productos o servicios y activa tu perfil.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    step: '02',
    title: 'Conecta con clientes cercanos',
    desc: 'Aparece en búsquedas locales relevantes. Clientes en tu zona te encuentran cuando más te necesitan.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    step: '03',
    title: '¡Vende más cada día!',
    desc: 'Aumenta tu visibilidad, recibe pedidos directos y haz crecer tu negocio con métricas en tiempo real.',
  },
];

function StepCard({
  step,
  color,
  delay,
}: {
  step: typeof consumerSteps[0];
  color: string;
  delay: number;
}) {
  return (
    <div
      className="reveal-hidden flex gap-5 group"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex-shrink-0 pt-1">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
          style={{ background: `${color}15`, border: `1px solid ${color}25`, color }}
        >
          {step.icon}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] font-black uppercase tracking-widest"
            style={{ color }}
          >
            Paso {step.step}
          </span>
        </div>
        <h3 className="text-xl font-bold text-white leading-tight">{step.title}</h3>
        <p className="text-muted leading-relaxed text-sm">{step.desc}</p>
      </div>
    </div>
  );
}

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const hiddenEls = entry.target.querySelectorAll('.reveal-hidden');
            hiddenEls.forEach((el) => el.classList.add('reveal-visible'));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-funciona" ref={sectionRef} className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, #001070 20%, #001070 80%, transparent 100%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center space-y-4 mb-20 reveal-hidden">
          <p className="text-[10px] font-black uppercase tracking-[0.5em] text-primary">¿Cómo funciona?</p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
            Simple para todos,{' '}
            <span className="gradient-text">poderoso para todos</span>
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto leading-relaxed">
            Ya seas consumidor o negocio local, AQUÍ APP está diseñada para que tu experiencia sea rápida, directa y efectiva.
          </p>
        </div>

        {/* Two-column parallel journeys */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Consumer journey */}
          <div
            className="reveal-hidden rounded-[32px] p-8 lg:p-10 border space-y-10"
            style={{
              background: 'linear-gradient(135deg, rgba(244,141,49,0.08) 0%, rgba(0,16,112,0.9) 100%)',
              borderColor: 'rgba(244,141,49,0.15)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ background: 'rgba(244,141,49,0.12)', color: '#F48D31', border: '1px solid rgba(244,141,49,0.2)' }}
              >
                Consumidor
              </div>
              <span className="text-2xl font-black text-white">BÚSCA, CONECTA Y COMPRA</span>
            </div>
            <div className="space-y-8">
              {consumerSteps.map((step, i) => (
                <StepCard key={i} step={step} color="#F48D31" delay={i * 100} />
              ))}
            </div>
            <a
              href="#registro"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest text-white hover:scale-105 transition-all"
              style={{ background: '#F48D31', boxShadow: '0 6px 24px rgba(244,141,49,0.35)' }}
            >
              ¡Quiero buscar cerca de mí!
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          {/* Partner journey */}
          <div
            className="reveal-hidden rounded-[32px] p-8 lg:p-10 border space-y-10"
            style={{
              background: 'linear-gradient(135deg, rgba(0,201,184,0.08) 0%, rgba(0,16,112,0.9) 100%)',
              borderColor: 'rgba(0,201,184,0.15)',
              transitionDelay: '150ms',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ background: 'rgba(0,201,184,0.12)', color: '#00C9B8', border: '1px solid rgba(0,201,184,0.2)' }}
              >
                Aliado
              </div>
              <span className="text-2xl font-black text-white">ÚNETE, CONECTA Y VENDE</span>
            </div>
            <div className="space-y-8">
              {partnerSteps.map((step, i) => (
                <StepCard key={i} step={step} color="#00C9B8" delay={i * 100 + 150} />
              ))}
            </div>
            <a
              href="#aliados"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest text-bg hover:scale-105 transition-all"
              style={{ background: '#00C9B8', boxShadow: '0 6px 24px rgba(0,201,184,0.35)' }}
            >
              ¡Quiero vender más!
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}